/**
 * Serverzustand der Ticketstruktur.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../lib/apiError';
import {
  addChecklist,
  clearReminder,
  createLink,
  createTask,
  deleteLink,
  deleteTask,
  getReminder,
  listChecklists,
  listLinks,
  listTasks,
  setReminder,
  toggleChecklistItem,
  updateTask,
  type ChecklistCreate,
  type LinkCreate,
  type ReminderSet,
  type TaskCreate,
  type TaskUpdate,
} from './api';

export const structureKeys = {
  tasks: (ticketId: string) => ['tickets', 'tasks', ticketId] as const,
  checklists: (ticketId: string) => ['tickets', 'checklists', ticketId] as const,
  links: (ticketId: string) => ['tickets', 'links', ticketId] as const,
  reminder: (ticketId: string) => ['tickets', 'reminder', ticketId] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useTasks(ticketId: string) {
  return useQuery({
    queryKey: structureKeys.tasks(ticketId),
    queryFn: () => listTasks(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useChecklists(ticketId: string) {
  return useQuery({
    queryKey: structureKeys.checklists(ticketId),
    queryFn: () => listChecklists(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useLinks(ticketId: string) {
  return useQuery({
    queryKey: structureKeys.links(ticketId),
    queryFn: () => listLinks(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useReminder(ticketId: string) {
  return useQuery({
    queryKey: structureKeys.reminder(ticketId),
    queryFn: () => getReminder(ticketId),
    retry: retryUnlessPermanent,
  });
}

function useInvalidate(key: readonly string[]) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: key });
  };
}

export function useCreateTask(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.tasks(ticketId));
  return useMutation({
    mutationFn: (payload: TaskCreate) => createTask(ticketId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateTask(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.tasks(ticketId));
  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: TaskUpdate }) =>
      updateTask(ticketId, taskId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteTask(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.tasks(ticketId));
  return useMutation({
    mutationFn: (taskId: string) => deleteTask(ticketId, taskId),
    onSuccess: invalidate,
  });
}

export function useAddChecklist(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.checklists(ticketId));
  return useMutation({
    mutationFn: (payload: ChecklistCreate) => addChecklist(ticketId, payload),
    onSuccess: invalidate,
  });
}

export function useToggleChecklistItem(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.checklists(ticketId));
  return useMutation({
    mutationFn: ({ itemId, isDone }: { itemId: string; isDone: boolean }) =>
      toggleChecklistItem(ticketId, itemId, isDone),
    onSuccess: invalidate,
  });
}

export function useCreateLink(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.links(ticketId));
  return useMutation({
    mutationFn: (payload: LinkCreate) => createLink(ticketId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteLink(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.links(ticketId));
  return useMutation({
    mutationFn: (linkId: string) => deleteLink(ticketId, linkId),
    onSuccess: invalidate,
  });
}

export function useSetReminder(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.reminder(ticketId));
  return useMutation({
    mutationFn: (payload: ReminderSet) => setReminder(ticketId, payload),
    onSuccess: invalidate,
  });
}

export function useClearReminder(ticketId: string) {
  const invalidate = useInvalidate(structureKeys.reminder(ticketId));
  return useMutation({
    mutationFn: () => clearReminder(ticketId),
    onSuccess: invalidate,
  });
}
